import { ValidationError, ProcessedValidationError } from '../types';

// Map backend section names to step IDs
const sectionToStepMapping: Record<string, string> = {
  'APPLICATION_INFORMATION': 'contest-info',
  'PROJECT_INFORMATION': 'basic',
  'PROJECT_DESCRIPTION': 'content',
  'PROJECT_TEAM': 'team',
  'PROJECT_RESOURCES': 'budget',
  'PROJECT_ATTACHMENTS': 'publications',
  'PROJECT_SUMMARY': 'review',
};

// Map step IDs to user-friendly section titles
const stepToTitleMapping: Record<string, string> = {
  'contest-info': 'Información de Concurso',
  'basic': 'Antecedentes',
  'content': 'Formulación',
  'team': 'Equipo de Trabajo', 
  'budget': 'Recursos',
  'publications': 'Publicaciones y Anexos',
  'review': 'Revisión y Envío',
};

// Map field paths to user-friendly labels
const fieldToLabelMapping: Record<string, string> = {
  // Basic information fields
  'projectTitle': 'Título del proyecto',
  'evaluationArea': 'Línea de investigación de la propuesta',
  'principalInvestigator.name': 'Nombre del investigador responsable',
  'principalInvestigator.email': 'Email del investigador responsable',
  'principalInvestigator.run': 'RUN del investigador responsable',
  
  // Team fields
  'coInvestigators': 'Co-investigadores',
  
  // Content fields
  'executiveSummary': 'Resumen ejecutivo',
  'projectDocument': 'Documento del proyecto',
  
  // Budget fields
  'budget.detailedFile': 'Archivo de presupuesto detallado',
  
  // Publications fields - dynamic patterns
  'cvNarrative': 'Narrativa del CV',
  'publications': 'Publicaciones',
  'principalInvestigatorDoiFile': 'DOI del Investigador Responsable',
  'coInvestigatorDoiFile': 'DOI del Investigador Principal',
  
  // Declarations
  'informationDeclaration': 'Declaración de información',
  'hasConflictOfInterest': 'Declaración de conflicto de interés',
};

// Helper function to extract investigator name and index from field path
function extractInvestigatorInfo(fieldPath: string): { investigatorName: string; index: number } | null {
  const match = fieldPath.match(/ipCvData\[(\d+)\]/);
  if (match) {
    const index = parseInt(match[1], 10);
    // For now, we'll use generic names. In a real app, you might want to 
    // fetch the actual investigator names from the form data
    const investigatorName = index === 0 ? 'Investigador Responsable' : `Investigador Principal ${index}`;
    return { investigatorName, index };
  }
  return null;
}

// Generate field label from field path
function generateFieldLabel(fieldPath: string): string {
  // Handle array notation for ipCvData
  if (fieldPath.includes('ipCvData[')) {
    const investigatorInfo = extractInvestigatorInfo(fieldPath);
    
    if (fieldPath.includes('.cvNarrative')) {
      return investigatorInfo 
        ? `Narrativa del CV - ${investigatorInfo.investigatorName}`
        : 'Narrativa del CV';
    }
    
    if (fieldPath.includes('.publications')) {
      return investigatorInfo
        ? `Publicaciones - ${investigatorInfo.investigatorName}`
        : 'Publicaciones';
    }
  }
  
  // Handle co-investigator array notation
  if (fieldPath.includes('coInvestigators[')) {
    const match = fieldPath.match(/coInvestigators\[(\d+)\]\.(.+)/);
    if (match) {
      const index = parseInt(match[1], 10);
      const field = match[2];
      const coInvNumber = index + 1;
      
      const fieldLabels: Record<string, string> = {
        'name': 'Nombre',
        'email': 'Email',
        'run': 'RUN',
        'affiliation': 'Afiliación',
        'commitmentLetter': 'Carta de compromiso',
      };
      
      const fieldLabel = fieldLabels[field] || field;
      return `${fieldLabel} - Co-investigador ${coInvNumber}`;
    }
  }
  
  // Check direct mapping
  if (fieldToLabelMapping[fieldPath]) {
    return fieldToLabelMapping[fieldPath];
  }
  
  // Handle nested field paths
  for (const [key, label] of Object.entries(fieldToLabelMapping)) {
    if (fieldPath.includes(key)) {
      return label;
    }
  }
  
  // Fallback: format the field path to be more readable
  return fieldPath
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .replace(/\[(\d+)\]/g, ' $1')
    .replace(/\./g, ' - ');
}

// Process validation errors from backend
export function processValidationErrors(
  errors: ValidationError[]
): ProcessedValidationError[] {
  return errors.map(error => {
    const stepId = sectionToStepMapping[error.section] || error.section.toLowerCase();
    const sectionTitle = stepToTitleMapping[stepId] || error.section;
    const fieldLabel = generateFieldLabel(error.field);
    
    return {
      field: error.field,
      message: error.message,
      code: error.code,
      section: stepId,
      sectionTitle,
      fieldLabel,
    };
  });
}

// Helper function to find the first section with errors for navigation
export function findFirstErrorSection(
  processedErrors: ProcessedValidationError[],
  steps: { id: string; title: string }[]
): number {
  if (processedErrors.length === 0) return -1;
  
  const firstErrorSection = processedErrors[0].section;
  return steps.findIndex(step => step.id === firstErrorSection);
}